import { createCloseButton } from './block-builder';
import type { FormField, GuideRenderContext } from './types';

/**
 * Construcción COMPARTIDA del contenido de una guía `form` (overlay) e
 * `inline-form` (embebida): título/texto, campos, validación de requeridos,
 * envío vía `context.onFormSubmit`, estado "¡Gracias!" y cierre diferido.
 * El caller pone el chrome (modal-card u tarjeta inline) y pasa la `card`
 * donde se reemplaza el contenido por el agradecimiento.
 *
 * Mismas reglas de seguridad que block-builder: cero `innerHTML`, todo texto
 * por `textContent`.
 */
export function mountFormContent(
  card: HTMLElement,
  context: GuideRenderContext,
  doc: Document,
): void {
  const step = context.guide.guideSteps[0];
  if (!step) return;
  const fields = step.fields ?? [];

  const dismiss = (): void => {
    context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'dismissed' });
    context.onClose();
  };

  const content = doc.createElement('div');
  content.className = 'veo-guide-content';

  if (step.title) {
    const heading = doc.createElement('h2');
    heading.className = 'veo-guide-title';
    heading.textContent = step.title;
    content.appendChild(heading);
  }
  if (step.content) {
    const paragraph = doc.createElement('p');
    paragraph.className = 'veo-guide-text';
    paragraph.textContent = step.content;
    content.appendChild(paragraph);
  }

  const readers: Array<{ field: FormField; read: () => unknown }> = [];
  const form = doc.createElement('form');
  form.className = 'veo-form';
  form.noValidate = true;
  for (const field of fields) {
    const { wrapper, read } = buildField(field, doc);
    readers.push({ field, read });
    form.appendChild(wrapper);
  }

  const error = doc.createElement('p');
  error.className = 'veo-form-error';
  error.style.display = 'none';
  form.appendChild(error);

  const actions = doc.createElement('div');
  actions.className = 'veo-guide-actions';
  const submit = doc.createElement('button');
  submit.type = 'submit';
  submit.className = 'veo-guide-cta';
  submit.textContent = step.ctaText || 'Enviar';
  actions.appendChild(submit);
  form.appendChild(actions);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    error.style.display = 'none';

    const answers: Record<string, unknown> = {};
    for (const { field, read } of readers) {
      const value = read();
      const empty = value === undefined || value === null || value === '';
      if (empty) {
        if (field.required) {
          error.textContent = `Completa "${field.label}"`;
          error.style.display = 'block';
          return;
        }
        continue;
      }
      answers[field.key] = value;
    }
    if (Object.keys(answers).length === 0) {
      error.textContent = 'Responde al menos un campo';
      error.style.display = 'block';
      return;
    }

    submit.disabled = true;
    const submitFn = context.onFormSubmit ?? (() => Promise.resolve(true));
    void submitFn(answers).then((ok) => {
      if (!ok) {
        submit.disabled = false;
        error.textContent = 'No se pudo enviar. Prueba de nuevo.';
        error.style.display = 'block';
        return;
      }
      context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'completed' });
      const note = context.isPreview
        ? (context.previewNote ?? 'Vista previa: la respuesta NO se guardó.')
        : null;
      showThanks(card, doc, note);
      window.setTimeout(() => context.onClose(), note ? 2200 : 1400);
    });
  });

  content.appendChild(form);
  content.appendChild(createCloseButton(doc, dismiss));

  card.appendChild(content);
}

/** Reemplaza el contenido de la tarjeta por un "¡Gracias!" breve. */
function showThanks(card: HTMLElement, doc: Document, note: string | null): void {
  while (card.firstChild) card.removeChild(card.firstChild);
  const thanks = doc.createElement('div');
  thanks.className = 'veo-form-thanks';
  thanks.textContent = '✓ ¡Gracias por tu respuesta!';
  card.appendChild(thanks);
  if (note) {
    const noteEl = doc.createElement('p');
    noteEl.className = 'veo-form-preview-note';
    noteEl.textContent = note;
    card.appendChild(noteEl);
  }
}

/** Construye el DOM de un campo y devuelve su lector de valor. */
export function buildField(
  field: FormField,
  doc: Document,
): { wrapper: HTMLElement; read: () => unknown } {
  const wrapper = doc.createElement('div');
  wrapper.className = 'veo-form-field';

  const label = doc.createElement('label');
  label.className = 'veo-form-label';
  label.textContent = field.required ? `${field.label} *` : field.label;
  wrapper.appendChild(label);

  switch (field.type) {
    case 'textarea': {
      const input = doc.createElement('textarea');
      input.className = 'veo-form-input';
      input.rows = 3;
      if (field.placeholder) input.placeholder = field.placeholder;
      wrapper.appendChild(input);
      return { wrapper, read: () => input.value.trim() };
    }
    case 'number': {
      const input = doc.createElement('input');
      input.type = 'number';
      input.className = 'veo-form-input';
      if (field.placeholder) input.placeholder = field.placeholder;
      wrapper.appendChild(input);
      return {
        wrapper,
        read: () => (input.value.trim() === '' ? undefined : Number(input.value)),
      };
    }
    case 'select': {
      const select = doc.createElement('select');
      select.className = 'veo-form-input';
      const blank = doc.createElement('option');
      blank.value = '';
      blank.textContent = field.placeholder || 'Elige una opción…';
      select.appendChild(blank);
      for (const opt of field.options ?? []) {
        const option = doc.createElement('option');
        option.value = opt;
        option.textContent = opt;
        select.appendChild(option);
      }
      wrapper.appendChild(select);
      return { wrapper, read: () => select.value || undefined };
    }
    case 'radio': {
      const group = doc.createElement('div');
      group.className = 'veo-form-options';
      const name = `veo-${field.key}`;
      const inputs: HTMLInputElement[] = [];
      for (const opt of field.options ?? []) {
        const optionLabel = doc.createElement('label');
        optionLabel.className = 'veo-form-option';
        const radio = doc.createElement('input');
        radio.type = 'radio';
        radio.name = name;
        radio.value = opt;
        inputs.push(radio);
        const text = doc.createElement('span');
        text.textContent = opt;
        optionLabel.appendChild(radio);
        optionLabel.appendChild(text);
        group.appendChild(optionLabel);
      }
      wrapper.appendChild(group);
      return { wrapper, read: () => inputs.find((i) => i.checked)?.value };
    }
    case 'multiselect': {
      // Grupo de checkboxes: la respuesta es un ARRAY con las opciones marcadas.
      const group = doc.createElement('div');
      group.className = 'veo-form-options';
      const inputs: HTMLInputElement[] = [];
      for (const opt of field.options ?? []) {
        const optionLabel = doc.createElement('label');
        optionLabel.className = 'veo-form-option';
        const checkbox = doc.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.value = opt;
        inputs.push(checkbox);
        const text = doc.createElement('span');
        text.textContent = opt;
        optionLabel.appendChild(checkbox);
        optionLabel.appendChild(text);
        group.appendChild(optionLabel);
      }
      wrapper.appendChild(group);
      return {
        wrapper,
        read: () => {
          const values = inputs.filter((i) => i.checked).map((i) => i.value);
          return values.length > 0 ? values : undefined;
        },
      };
    }
    case 'checkbox': {
      const optionLabel = doc.createElement('label');
      optionLabel.className = 'veo-form-option';
      const checkbox = doc.createElement('input');
      checkbox.type = 'checkbox';
      const text = doc.createElement('span');
      text.textContent = field.placeholder || 'Sí';
      optionLabel.appendChild(checkbox);
      optionLabel.appendChild(text);
      wrapper.appendChild(optionLabel);
      // Checkbox siempre responde true/false (no “vacío”): required pide marcarlo.
      return { wrapper, read: () => (checkbox.checked ? true : field.required ? '' : false) };
    }
    case 'yesno': {
      // Radio Sí/No: dos opciones visibles, pero la respuesta es BOOLEANA
      // (true/false) — el widget "amigable" para props de tipo sí/no.
      const group = doc.createElement('div');
      group.className = 'veo-form-options';
      const name = `veo-${field.key}`;
      const [yesLabel, noLabel] =
        field.options?.length === 2 ? (field.options as [string, string]) : ['Sí', 'No'];
      const inputs: Array<{ input: HTMLInputElement; value: boolean }> = [];
      for (const { label: optText, value } of [
        { label: yesLabel, value: true },
        { label: noLabel, value: false },
      ]) {
        const optionLabel = doc.createElement('label');
        optionLabel.className = 'veo-form-option';
        const radio = doc.createElement('input');
        radio.type = 'radio';
        radio.name = name;
        inputs.push({ input: radio, value });
        const text = doc.createElement('span');
        text.textContent = optText;
        optionLabel.appendChild(radio);
        optionLabel.appendChild(text);
        group.appendChild(optionLabel);
      }
      wrapper.appendChild(group);
      return { wrapper, read: () => inputs.find((i) => i.input.checked)?.value };
    }
    case 'nps':
      return buildScale(wrapper, doc, 0, 10, 'veo-nps-btn');
    case 'rating':
      return buildScale(wrapper, doc, 1, 5, 'veo-rating-btn', '★');
    default: {
      const input = doc.createElement('input');
      input.type = 'text';
      input.className = 'veo-form-input';
      if (field.placeholder) input.placeholder = field.placeholder;
      wrapper.appendChild(input);
      return { wrapper, read: () => input.value.trim() };
    }
  }
}

/** Fila de botones numerados (NPS 0-10) o de estrellas (rating 1-5). */
function buildScale(
  wrapper: HTMLElement,
  doc: Document,
  min: number,
  max: number,
  btnClass: string,
  symbol?: string,
): { wrapper: HTMLElement; read: () => unknown } {
  const row = doc.createElement('div');
  row.className = 'veo-form-scale';
  let selected: number | undefined;
  const buttons: HTMLButtonElement[] = [];
  for (let n = min; n <= max; n++) {
    const btn = doc.createElement('button');
    btn.type = 'button';
    btn.className = btnClass;
    btn.textContent = symbol ?? String(n);
    btn.setAttribute('aria-label', String(n));
    btn.addEventListener('click', () => {
      selected = n;
      buttons.forEach((b, i) => {
        // Rating pinta acumulado (★★★☆☆); NPS solo el elegido.
        const active = symbol ? i + min <= n : i + min === n;
        b.classList.toggle('veo-scale-active', active);
      });
    });
    buttons.push(btn);
    row.appendChild(btn);
  }
  wrapper.appendChild(row);
  return { wrapper, read: () => selected };
}
